import { ChangeEvent, FC, useCallback } from 'react';
import {
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import ModalDialog from '../../common/components/ModalDialog';
import { makeStyles } from 'tss-react/mui';
import isEmpty from 'lodash/isEmpty';
import ButtonGroup from '../../common/components/ButtonGroup';
import { setRemoveOrgVerificationValue } from '../store-actions/org-settings-store-actions';

const RemoveOrgModal: FC<
  React.PropsWithChildren<IRemoveOrgModalProps>
> = props => {
  const theme = useTheme();
  const {
    open,
    onClose,
    onRemove,
    isLoading,
    errorMessage,
    deleteVerified,
    deleteText,
  } = props;

  const { classes } = useStyles();

  const handleOnChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setRemoveOrgVerificationValue(value);
  }, []);

  const deleteButtonTooltip = deleteVerified
    ? `Press to delete organization`
    : `Type '${deleteText}' to confirm`;

  return (
    <ModalDialog
      modalColor={theme.palette.error.dark}
      open={open}
      onClose={onClose}
      modalHeader='Delete organization'>
      <Typography mb={2} component='div' flexGrow={1} variant='body2'>
        This action cannot be undone. This will permanently delete the
        organization and remove all it's data associations
      </Typography>
      <div className={classes.actions}>
        <div className='input'>
          <FormControl fullWidth color='error' margin='none' size='small'>
            <InputLabel variant='standard' htmlFor='verificationTextInput'>
              <Typography variant='body1'>Verfication Text</Typography>
            </InputLabel>
            <Input
              onChange={handleOnChange}
              placeholder={deleteText}
              name='verificationTextInput'
            />
            <FormHelperText margin='dense' variant='standard'>
              {!isEmpty(errorMessage) && (
                <Typography flexGrow={1} color='error.main' variant='caption'>
                  {errorMessage}
                </Typography>
              )}
              {isEmpty(errorMessage) && (
                <Typography flexGrow={1} color='error.main' variant='caption'>
                  Type "{deleteText}" to confirm
                </Typography>
              )}
            </FormHelperText>
          </FormControl>
        </div>
        <ButtonGroup
          onCancel={onClose}
          onClick={onRemove}
          isLoading={isLoading}
          buttonCancelLabel='Cancel'
          buttonCancelTooltip='Cancel this operation'
          buttonActionLabel='Delete'
          buttonActionTooltip={deleteButtonTooltip}
          buttonActionColor='error'
          isActionDisabled={!deleteVerified}
        />
      </div>
    </ModalDialog>
  );
};

interface IRemoveOrgModalProps {
  open: boolean;
  onClose: () => void;
  onRemove: () => void;
  isLoading: boolean;
  deleteVerified: boolean;
  deleteText?: string;
  errorMessage: string;
}

const useStyles = makeStyles()((theme: Theme) => ({
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'end',
    '& > .input': {
      width: '60%',
    },
    '& > .buttonGroup': {
      justifyContent: 'end',
      display: 'flex',
      flexDirection: 'row',
      width: '40%',
      gap: theme.spacing(1),
    },
  },
}));

export default RemoveOrgModal;
